import { Form, formProviderHOC } from '@sravni/cosago-react-library/lib/components';
import { useControlledNavigationSequenceControl, useFormContext } from '@sravni/cosago-react-library/lib/hooks';
import type { FormComponentProps } from '@sravni/cosago-react-library/lib/types';
import { Icon, Space, TextInput } from '@sravni/react-design-system';
import { Heading } from '@sravni/react-design-system/lib/Typography';
import { Tip } from '@sravni/react-icons';
import { useIsMobile } from '@sravni/react-utils';
import type { FC } from 'react';
import React from 'react';

import { useAppSelector } from 'shared/lib';
import type { TCommonAdditionalFormProps } from 'shared/types';
import { AnketaAlert } from 'shared/ui/AnketaAlert';
import { FormWithControlledNavigation } from 'shared/ui/FormWithControlledNavigation';

import { selectFullName } from 'entities/creditForm';
import type { TPledgeEstateFields } from 'entities/pledgeEstateForm';

import { usePledgeEstateFieldsControl } from '../lib';
import { FormFieldsNavigationSequence } from '../PledgeEstateForm.config';
import { PledgeEstateFormTexts } from '../PledgeEstateForm.texts';

type Props = FormComponentProps<TPledgeEstateFields, TCommonAdditionalFormProps>;

const PledgeEstateFormView: FC<Props> = ({ onSubmit, NAMES, submitButtonText, FieldConstructor, isLoading }) => {
  const controls = useControlledNavigationSequenceControl({ fields: FormFieldsNavigationSequence });
  const fullName = useAppSelector(selectFullName);
  const { watch } = useFormContext<TPledgeEstateFields>();
  const isOnlyOwner = watch(NAMES.isOnlyOwner);
  const isPledgeAddressSameRegistration = watch(NAMES.isPledgeAddressSameRegistration);
  const isMobile = useIsMobile();

  usePledgeEstateFieldsControl();

  return (
    <FormWithControlledNavigation
      onSubmit={onSubmit}
      controlElement={FieldConstructor}
      isDialog={isMobile}
      isLoading={isLoading}
      submitButtonText={submitButtonText}
      navigationControls={controls}
      isSubmitDisabled={isLoading}
    >
      <Form.Block>
        <Form.Row colWidths={[12]}>
          <AnketaAlert
            color="blue"
            variant="secondary"
            subtitle={PledgeEstateFormTexts.alertText}
            icon={
              <Icon
                size={20}
                icon={<Tip />}
              />
            }
          />
        </Form.Row>
        <Form.Row colWidths={[12]}>
          <Heading level={4}>Адрес залоговой недвижимости</Heading>
        </Form.Row>
        <Form.Row colWidths={[12]}>
          <FieldConstructor type={NAMES.isPledgeAddressSameRegistration} />
        </Form.Row>
        <Form.Row colWidths={[8, 4]}>
          <FieldConstructor
            type={NAMES.pledgeEstateAddress}
            isDisabled={isPledgeAddressSameRegistration}
          />
          <FieldConstructor
            type={NAMES.pledgeEstateAddressFlat}
            isDisabled={isPledgeAddressSameRegistration}
          />
        </Form.Row>
        <Form.Row colWidths={[12]}>
          <Space
            direction="vertical"
            size={16}
          >
            <Heading level={4}>Собственник</Heading>
            <TextInput
              label={PledgeEstateFormTexts.fio}
              disabled
              value={fullName?.label}
            />
          </Space>
        </Form.Row>
        <Form.Row colWidths={[12]}>
          <FieldConstructor type={NAMES.isOnlyOwner} />
        </Form.Row>
        {!isOnlyOwner && (
          <>
            <Form.Row colWidths={[12]}>
              <Heading level={4}>Добавьте информацию о другом собственнике</Heading>
            </Form.Row>
            <Form.Row colWidths={[12]}>
              <FieldConstructor
                type={NAMES.otherOwnerFullName}
                isDisabled={isOnlyOwner}
              />
            </Form.Row>
            <Form.Row colWidths={[6, 6]}>
              <FieldConstructor
                type={NAMES.otherOwnerPhone}
                isDisabled={isOnlyOwner}
              />
              <FieldConstructor
                type={NAMES.otherOwnerMaritalStatus}
                isDisabled={isOnlyOwner}
              />
            </Form.Row>
            <Form.Row colWidths={[6, 6]}>
              <FieldConstructor
                type={NAMES.relationship}
                isDisabled={isOnlyOwner}
              />
            </Form.Row>
          </>
        )}
      </Form.Block>
    </FormWithControlledNavigation>
  );
};

export const PledgeEstateFormViewHoc = formProviderHOC<TPledgeEstateFields, TCommonAdditionalFormProps>(
  PledgeEstateFormView,
);
